import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SubscriptionService } from './subscription.service';
import { ChargebeeService } from '../chargebee.service';
import {
  currencies,
  Feature,
  ModuleType,
  PlanType,
} from '../../../../../types-legacy/lib/types';
import { ClockHourService, StorageService } from '../../../services';
import { modulesInfo } from '../plan-selection/modulesInfo';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PlatformService } from '../../platform-disable-content/platform.service';
import { IosActivationComponent } from './ios-activation/ios-activation.component';
import { TrainingRequestComponent } from '../training-request/training-request.component';
import { AnalyticsService } from '@web-frontend/shared/services/analytics/analytics.service';
import AmplitudeEvents from 'src/types/amplitude.enum';
import { Subscription } from 'rxjs';

interface PlanSelection {
  id: string;
  name: string;
  price: string;
  pricePerYear: string;
  description?: string;
  features?: Array<{
    name: string;
    available?: boolean;
    description?: string;
  }>;
}

interface ModulePlans {
  [key: string]: {
    name: string;
    icon: string;
    type: ModuleType;
    description: string;
    startingPrice: string;
    isFullPlan: boolean;
    currency: string;
    plans: Array<PlanSelection>;
  };
}

@Component({
  selector: 'roma-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlansComponent implements OnInit, OnDestroy {
  @Input()
  feature: Feature.Purchasable;

  public isLoading = true;
  public modulePlansMonthly: ModulePlans;
  public modulePlansYearly: ModulePlans;
  public isSubscripting = false;
  public periodType: 'monthly' | 'yearly' = 'yearly';
  public sortedModules = [
    ModuleType.SALES,
    ModuleType.TIME_TRACKING,
    ModuleType.CRM,
    ModuleType.FULL,
  ];
  public discountValue = 15;
  private userEmail = JSON.parse(StorageService.userData ?? '{}')?.email;
  private currenciesAllowed = currencies.map(({ text }) => text);
  // TODO: remove when backend delete initial plan
  private planToHide = 'V1-TIME-TRACKING-INITIAL';
  private planNames = {
    BASIC: 'BASIC',
    PRO: 'PRO',
  };

  private whatsappSub?: Subscription;

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private cdRef: ChangeDetectorRef,
    private readonly chargebee: ChargebeeService,
    private dialogRef: MatDialogRef<PlansComponent>,
    private dialog: MatDialog,
    private platformService: PlatformService,
    private readonly analyticsService: AnalyticsService,
    private readonly clockHourService: ClockHourService
  ) {}

  get modules(): ModulePlans {
    return this.periodType === 'monthly'
      ? this.modulePlansMonthly
      : this.modulePlansYearly;
  }

  async ngOnInit(): Promise<void> {
    const response = await this.subscriptionService.getItemPrices();
    if (response && response.planTypes) {
      this.preparePlans(response.planTypes);
      this.draw();
    }
  }

  public subscribe(event: {
    plan: PlanType;
    isWhatsAppAddonEnabled: boolean;
  }): void {
    const { plan, isWhatsAppAddonEnabled } = event;

    if (!this.userEmail) {
      throw new Error('User email not found');
    }
    if (this.platformService.isIos() || this.platformService.isAndroid()) {
      this.dialog.open<IosActivationComponent>(IosActivationComponent);
      return;
    }
    this.dialog.open<TrainingRequestComponent>(TrainingRequestComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      disableClose: true,
    });

    const planTypeId = plan.id;
    this.isSubscripting = true;

    this.chargebee
      .openCheckout(this.userEmail, planTypeId, false)
      .then((res) => {
        if (res?.success) {
          this.handleWhatsAppAddon(plan.id, isWhatsAppAddonEnabled);
          this.onClose();
        }
      })
      .finally(() => {
        this.isSubscripting = false;
        this.draw();
      });
  }

  private handleWhatsAppAddon(
    planId: string,
    isWhatsAppAddonEnabled: boolean
  ): void {
    const isTimeTracking = planId.includes('TIME-TRACKING');
    if (isTimeTracking && isWhatsAppAddonEnabled) {
      this.analyticsService.trackEvent({
        sources: ['amplitude', 'braze'],
        eventName: AmplitudeEvents.whatsapp_stopwatch_enabled,
        eventProperties: {
          source: 'Pricing',
        },
      });
      this.whatsappSub = this.clockHourService
        .updateWhatsAppAddonStatus(
          StorageService.CompanyId,
          isWhatsAppAddonEnabled
        )
        .subscribe();
    }
  }

  public changePeriodType(type: 'monthly' | 'yearly'): void {
    this.periodType = type;
    this.draw();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  private draw() {
    this.cdRef.detectChanges();
  }

  private preparePlans(responsePlans: PlanType[]): void {
    this.sortedModules.forEach((moduleType: ModuleType) => {
      const subscriptionsMonthly = responsePlans.filter(
        ({ id, period_unit, item_id }) => {
          return (
            item_id !== this.planToHide &&
            id.includes(moduleType) &&
            period_unit === 'month'
          );
        }
      );
      const subscriptionsYearly = responsePlans.filter(
        ({ id, period_unit, item_id }) => {
          return (
            item_id !== this.planToHide &&
            id.includes(moduleType) &&
            period_unit === 'year'
          );
        }
      );

      const plansMonthly = this.preparePlan(subscriptionsMonthly);
      const plansYearly = this.preparePlan(subscriptionsYearly);
      const moduleInfo = modulesInfo.find(
        (module) => module.type === moduleType
      );

      const moduleBase = {
        name: moduleInfo.name,
        icon: moduleInfo.icon,
        type: moduleType,
        description: moduleInfo.description,
        isFullPlan: moduleType === ModuleType.FULL,
        currency: this.getCurrencySymbol(subscriptionsMonthly[0].currency_code),
      };

      this.modulePlansMonthly = {
        ...this.modulePlansMonthly,
        [moduleType]: {
          ...moduleBase,
          plans: plansMonthly,
          startingPrice: plansMonthly[0].price,
          features: moduleInfo.features,
        },
      } as ModulePlans;

      this.modulePlansYearly = {
        ...this.modulePlansYearly,
        [moduleType]: {
          ...moduleBase,
          plans: plansYearly,
          startingPrice: plansYearly[0].price,
          features: moduleInfo.features,
        },
      } as ModulePlans;
    });
    this.isLoading = false;
  }

  private preparePlan(plan: PlanType[]): Array<PlanSelection> {
    return plan
      .map((planType) => {
        return {
          id: planType.id,
          name: this.getPlanName(planType.id),
          price: `${planType.monthly}`,
          pricePerYear: `${planType.price}`,
        };
      })
      .sort((a, b) => {
        return a.id.includes(this.planNames.BASIC) &&
          !b.id.includes(this.planNames.BASIC)
          ? -1
          : 1;
      });
  }

  private getPlanName(planId: string): string {
    if (planId.includes(this.planNames.BASIC)) {
      return this.planNames.BASIC.toLowerCase();
    }
    return this.planNames.PRO.toLowerCase();
  }

  private getCurrencySymbol(currencyCode: string): string {
    if (!currencyCode || !this.currenciesAllowed.includes(currencyCode)) {
      return '';
    }

    return currencies.find(
      ({ text }: { text: string }) => text === currencyCode
    )?.value;
  }

  ngOnDestroy(): void {
    this.whatsappSub?.unsubscribe();
  }
}
